import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useEmployees } from "../../hooks/employees";
import { useSpecialAssignmentsReport } from "../../hooks/assignments";
import { useTranslation } from "react-i18next";
import Table from "../../components/ReactTable/Table";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { buildEmployeeDetailInformationRoute } from "../../routes/AdminRoutes";
import EmployeesFilters from "./EmployeesFilters";
import usePermissions from "../../redux/selectors/usePermissions";
import useUser from "../../hooks/useUser";
import { useTableData } from "../../components/ReactTable/useTableData";
import TooltipButton from "../../components/Buttons/TooltipButton";

const columns = (t, isClient) => {
  const divisionColumn = {
    Header: t("table.columnHeader.OwnedBy"),
    accessor: "customer.name",
    Filter: TextFilter,
    canSort: true,
    filterPlaceholder: t("table.filterPlaceholder.ownedByAll"),
    id: "customers.name"
  };
  const columns = [
    {
      Header: t("table.columnHeader.name"),
      accessor: "fullName",
      Filter: TextFilter,
      filterPlaceholder: t("table.filterPlaceholder.nameAll"),
      canSort: true,
      id: "full_name"
    },
    {
      Header: t("table.columnHeader.location"),
      accessor: "location.name",
      Filter: TextFilter,
      canSort: true,
      filterPlaceholder: t("table.filterPlaceholder.locationAll"),
      id: "locations.name"
    },
    {
      Header: t("table.columnHeader.position"),
      accessor: "position.name",
      Filter: TextFilter,
      canSort: true,
      filterPlaceholder: t("table.filterPlaceholder.positionAll"),
      id: "positions.name"
    }
  ];
  isClient && columns.push(divisionColumn);
  return columns;
};

const Employees = () => {
  const { isGroupLeader, isClient } = useUser();
  const { isEmployeeManager, isAdmin, isGoldOrPlatinum } = usePermissions();

  const report = useSpecialAssignmentsReport();

  // Only show users belonging to the user's group if they are Manage Employees only and they are a group leader
  const showGroupUsersOnly = !isAdmin && isEmployeeManager && isGroupLeader;
  // Only show users belonging to the user's location if they are Manage Employees only and they are not a group leader
  const showLocationUsersOnly = !isAdmin && isEmployeeManager && !isGroupLeader;

  const permissionsFilters = useMemo(
    () => ({
      onlyGroupUsers: showGroupUsersOnly,
      onlyLocationUsers: showLocationUsersOnly
    }),
    []
  );

  const { getEmployees } = useEmployees();
  const {
    data: employees,
    onStateChange,
    isFetching,
    paginationState,
    onApplyExternalFilters
  } = useTableData(getEmployees, {
    externalFilters: permissionsFilters,
    page: 1,
    pageSize: 50
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const memoColumns = useMemo(() => columns(t, isClient), []);

  const submitExternalFilter = exFilters => {
    onApplyExternalFilters({ ...permissionsFilters, ...exFilters });
  };

  const handleRowClick = (e, rowData) => {
    dispatch(push(buildEmployeeDetailInformationRoute(rowData.row.id)));
  };

  return (
    <AdminPageLayout
      title="Employees"
      headerActions={
        <TooltipButton
          color="primary"
          variant="outlined"
          onClick={report}
          tooltipProps={{
            title: isGoldOrPlatinum
              ? ""
              : t("administration.goldPlatinumFeatureTooltip")
          }}
          disabled={!isGoldOrPlatinum}
        >
          {t("specialAssignments.Individual/SpecialAssignment")}
        </TooltipButton>
      }
    >
      <Box my={3}>
        <EmployeesFilters onSubmit={submitExternalFilter} />
      </Box>
      <Table
        data={employees}
        columns={memoColumns}
        onStateChange={onStateChange}
        onRowClick={handleRowClick}
        cellSize="medium"
        paginationState={paginationState}
        paginated={true}
        loading={isFetching}
      />
    </AdminPageLayout>
  );
};

export default Employees;
